'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export type GatePopoverSide = 'top' | 'bottom' | 'left' | 'right';
export type GatePopoverAlign = 'start' | 'center' | 'end';

export interface GatePopoverProps {
  /**
   * Target element the popover anchors against. When `null` the
   * popover centers on the viewport — useful when a trigger can't
   * forward its ref (keyboard shortcut, programmatic open, etc.).
   */
  anchorEl: HTMLElement | null;
  /** Controlled open state. */
  open: boolean;
  /** Fires on Escape keydown or click outside the popover. */
  onClose: () => void;
  /** Preferred side of the anchor to render on. Default `"bottom"`. */
  side?: GatePopoverSide;
  /** Alignment along the perpendicular axis. Default `"center"`. */
  align?: GatePopoverAlign;
  /**
   * Pixels between the anchor and the popover. Default `10`.
   */
  offset?: number;
  /** Popover content. Rendered inside a portal attached to `document.body`. */
  children: ReactNode;
  /** A11y label for the popover container. */
  ariaLabel?: string;
}

interface Position {
  top: number;
  left: number;
}

const VIEWPORT_MARGIN = 8;

function resolveSide(
  anchor: DOMRect,
  popover: { width: number; height: number },
  requested: GatePopoverSide,
  offset: number,
  viewport: { width: number; height: number }
): GatePopoverSide {
  if (requested === 'bottom' || requested === 'top') {
    const needed = popover.height + offset + VIEWPORT_MARGIN;
    const spaceBelow = viewport.height - anchor.bottom;
    const spaceAbove = anchor.top;
    if (requested === 'bottom' && spaceBelow < needed && spaceAbove > spaceBelow) return 'top';
    if (requested === 'top' && spaceAbove < needed && spaceBelow > spaceAbove) return 'bottom';
    return requested;
  }
  const needed = popover.width + offset + VIEWPORT_MARGIN;
  const spaceRight = viewport.width - anchor.right;
  const spaceLeft = anchor.left;
  if (requested === 'right' && spaceRight < needed && spaceLeft > spaceRight) return 'left';
  if (requested === 'left' && spaceLeft < needed && spaceRight > spaceLeft) return 'right';
  return requested;
}

function computePosition(
  anchor: DOMRect,
  popover: { width: number; height: number },
  requestedSide: GatePopoverSide,
  align: GatePopoverAlign,
  offset: number,
  viewport: { width: number; height: number }
): Position & { side: GatePopoverSide } {
  const side = resolveSide(anchor, popover, requestedSide, offset, viewport);

  let top = 0;
  let left = 0;

  if (side === 'bottom' || side === 'top') {
    top = side === 'bottom' ? anchor.bottom + offset : anchor.top - popover.height - offset;
    if (align === 'start') left = anchor.left;
    else if (align === 'end') left = anchor.right - popover.width;
    else left = anchor.left + (anchor.width - popover.width) / 2;
  } else {
    left = side === 'right' ? anchor.right + offset : anchor.left - popover.width - offset;
    if (align === 'start') top = anchor.top;
    else if (align === 'end') top = anchor.bottom - popover.height;
    else top = anchor.top + (anchor.height - popover.height) / 2;
  }

  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, viewport.width - popover.width - VIEWPORT_MARGIN)
  );
  top = Math.max(
    VIEWPORT_MARGIN,
    Math.min(top, viewport.height - popover.height - VIEWPORT_MARGIN)
  );
  return { top, left, side };
}

/**
 * Anchored portal popover. Positions itself against `anchorEl` on
 * every scroll / resize; closes on Escape or outside click. Purely
 * presentational — `open` is controlled by the caller. Radix-free
 * so the gate module stays extractable.
 *
 * Accessibility: focus is moved into the popover on open and
 * restored to the previously-focused element on close. Not a modal —
 * focus is not trapped.
 */
export function GatePopover({
  anchorEl,
  open,
  onClose,
  side = 'bottom',
  align = 'center',
  offset = 10,
  children,
  ariaLabel,
}: GatePopoverProps): ReactElement | null {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [resolvedSide, setResolvedSide] = useState<GatePopoverSide>(side);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const updatePosition = useCallback(() => {
    const el = popoverRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (anchorEl === null) {
      // Centered fallback when no anchor — no flip possible.
      setPosition({
        top: (vh - rect.height) / 2,
        left: (vw - rect.width) / 2,
      });
      setResolvedSide(side);
      return;
    }

    const anchorRect = anchorEl.getBoundingClientRect();
    const next = computePosition(
      anchorRect,
      { width: rect.width, height: rect.height },
      side,
      align,
      offset,
      { width: vw, height: vh }
    );
    setPosition({ top: next.top, left: next.left });
    setResolvedSide(next.side);
  }, [anchorEl, side, align, offset]);

  // Reposition on open + resize + scroll. `mounted` must be in the deps
  // so positioning re-runs once the portal actually attaches — otherwise
  // the first layout pass reads a null ref and the popover sits at
  // -9999px until a scroll event forces a recompute.
  useLayoutEffect(() => {
    if (!open || !mounted) return;
    updatePosition();
  }, [open, mounted, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handle = () => updatePosition();
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [open, updatePosition]);

  // Lock body scroll while open. Pads the body to compensate for the
  // removed scrollbar so the underlying layout doesn't jump.
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  // Outside click + Escape close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      const el = popoverRef.current;
      const target = e.target as Node | null;
      if (el === null || target === null) return;
      if (el.contains(target)) return;
      if (anchorEl !== null && anchorEl.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, onClose, anchorEl]);

  // Focus management.
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      const el = popoverRef.current;
      if (el !== null) {
        const focusable = el.querySelector<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable !== null) focusable.focus();
        else el.focus();
      }
      return;
    }
    const prev = previousFocusRef.current;
    if (prev !== null && document.contains(prev)) prev.focus();
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="tb-gate-popover"
      data-open={open ? 'true' : 'false'}
      data-side={resolvedSide}
      data-align={align}
      role="dialog"
      aria-modal="false"
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      tabIndex={-1}
      style={
        position !== null
          ? { top: `${position.top}px`, left: `${position.left}px` }
          : { top: '-9999px', left: '-9999px' }
      }
    >
      {anchorEl !== null ? <div className="tb-gate-arrow" aria-hidden="true" /> : null}
      {children}
    </div>,
    document.body
  );
}
